import { clsx } from 'clsx'
import { useContext } from 'react'

import { type ctaArgType, useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'

import { isUnresolvedLink } from '@contentful-types/content-types'
import { type HeroTitleSize, type TypeSectionHeroEntry } from '@contentful-types/section-hero'

import { SectionHeroLayoutDirection } from '@constants/enums/page-section'

import { SectionContext } from '@contexts/section-context'

import { CtaButtonDescription } from '@components/cta/cta-button/cta-button'
import { ContentRenderer } from '@components/renderer'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { SocialShareWidget } from '@components/social-share-widget/social-share-widget'
import { Column } from '@components/ui-core/column/column'
import { Text, type TextVariant } from '@components/ui-core/text/text'

import styles from './hero.module.scss'

export const getTextVariantForTitleSize = (titleSize?: HeroTitleSize): TextVariant => {
  switch (titleSize) {
    case 'H.L': {
      return 'headerLarge'
    }
    case 'H.XL': {
      return 'headerXlarge'
    }
    default: {
      return 'headerLarge'
    }
  }
}

export const getDescriptionTextVariantForTitleSize = (titleSize?: HeroTitleSize): TextVariant => {
  switch (titleSize) {
    case 'H.L': {
      return 'bodyLarge'
    }
    case 'H.XL': {
      return 'bodyLarge'
    }
    default: {
      return 'bodyLarge'
    }
  }
}

export const HeroTitle = ({ data }: { data: TypeSectionHeroEntry }) => {
  const { layoutDirection, title, titleSize, seoTitle, description, ctAs, socialShareWidget } =
    data?.fields

  const { isH1Section } = useContext(SectionContext)
  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()

  const titleTextVariant = getTextVariantForTitleSize(titleSize)
  const renderTitleAs = isH1Section ? 'h1' : 'h2'

  return (
    <figcaption>
      <Column gap="6x">
        <header className={styles.heroHeader}>
          {seoTitle ? (
            <>
              <Text as={renderTitleAs} variant="bodyMedium">
                {seoTitle}
              </Text>
              <Text variant={titleTextVariant} as="h2">
                {renderRichTextMultiLineContent(title)}
              </Text>
            </>
          ) : (
            <Text variant={titleTextVariant} as={renderTitleAs}>
              {renderRichTextMultiLineContent(title)}
            </Text>
          )}
          <Text variant={getDescriptionTextVariantForTitleSize(titleSize)}>
            {renderRichTextMultiLineContent(description)}
          </Text>
        </header>

        {ctAs?.length && (
          <>
            <div
              className={clsx(styles.ctasContainer, {
                [styles.ctasCentered]:
                  layoutDirection === SectionHeroLayoutDirection.Vertical ||
                  layoutDirection === SectionHeroLayoutDirection.VerticalWide,
              })}
            >
              <ContentRenderer data={ctAs.filter(Boolean) as Array<ctaArgType>} />
            </div>
            <div className={styles.ctasDescriptionsContainer}>
              {ctAs
                .map(variationContainerOrCta =>
                  getAuthAndDeviceTypeCta(variationContainerOrCta as ctaArgType),
                )
                .filter(Boolean)
                .filter(cta => !isUnresolvedLink(cta))
                .map((cta, index) => (
                  <CtaButtonDescription
                    key={`description-${cta.sys?.contentType?.sys?.id ?? `missing-content-type-${index}`}-${cta.sys?.id ?? `missing-id-${index}`}`}
                    data={cta}
                  />
                ))}
            </div>
          </>
        )}

        {socialShareWidget && <SocialShareWidget data={socialShareWidget} />}
      </Column>
    </figcaption>
  )
}
